import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';

/**
 * GettingStartedChecklist — Slides in from the right.
 * Shows 8 items for new users to complete post-onboarding.
 */
export const CHECKLIST_ITEMS = [
  { key: 'create_quote', label: 'Create your first quote', path: '/quotes/add', description: 'Build a quote for a customer' },
  { key: 'add_customer', label: 'Add a customer', path: '/customers?add=true', description: 'Add your first customer record' },
  { key: 'setup_tc', label: 'Set up Terms & Conditions', path: '/settings/tc-templates', description: 'Configure default T&C for quotes' },
  { key: 'configure_tax', label: 'Configure tax settings', path: '/settings/tax', description: 'Set your state and tax rates' },
  { key: 'invite_team', label: 'Invite a team member', path: '/settings/users', description: 'Add colleagues to your workspace' },
  { key: 'setup_margins', label: 'Configure default margins', path: '/settings/margins', description: 'Set profit margins by category' },
  { key: 'create_crew', label: 'Create a crew', path: '/settings/crews', description: 'Organize your field teams' },
  { key: 'explore_reports', label: 'View reports', path: '/reports', description: 'Check out the analytics dashboard' },
];

export default function GettingStartedChecklist({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [completedItems, setCompletedItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('bidit_checklist_completed') || '[]');
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('bidit_checklist_completed', JSON.stringify(completedItems));
  }, [completedItems]);

  const toggleItem = (key) => {
    setCompletedItems(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const completedCount = completedItems.length;
  const totalCount = CHECKLIST_ITEMS.length;
  const progress = (completedCount / totalCount) * 100;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Getting Started</h2>
            <p className="text-xs text-gray-500">{completedCount} of {totalCount} complete</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Checklist items */}
        <div className="flex-1 overflow-auto px-6 py-2">
          <ul className="space-y-2">
            {CHECKLIST_ITEMS.map(item => {
              const isDone = completedItems.includes(item.key);
              return (
                <li
                  key={item.key}
                  className={`flex items-start gap-3 p-3 rounded-md border border-[color:var(--color-border)] cursor-pointer transition-all ${
                    isDone
                      ? 'bg-green-50 border-green-200'
                      : 'bg-white border-[color:var(--color-border)] hover:border-primary-200 hover:bg-primary-50/30'
                  }`}
                  onClick={() => {
                    if (!isDone) {
                      navigate(item.path);
                      onClose();
                    }
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleItem(item.key); }}
                    className="mt-0.5 flex-shrink-0"
                  >
                    {isDone ? (
                      <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <CheckCircleIcon className="h-5 w-5 text-gray-300" />
                    )}
                  </button>
                  <div>
                    <p className={`text-sm font-medium ${isDone ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                      {item.label}
                    </p>
                    <p className="text-xs text-gray-400">{item.description}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Footer */}
        {completedCount === totalCount && (
          <div className="px-6 py-4 border-t bg-green-50 text-center">
            <p className="text-sm font-medium text-green-700">All done! You're a BIDIT pro.</p>
            <button onClick={onClose} className="text-xs text-green-600 hover:text-green-800 mt-1 underline">Dismiss</button>
          </div>
        )}
      </div>
    </>
  );
}
